import { LogoWithTextColorised } from "#web/assets/LogoWithTextColorised";
import { CardSkeleton } from "#web/components/common/CardSkeleton";
import { makeShop, ShopQuery } from "#web/components/shop/queries/shopQuery";
import { routeConfig } from "#web/configs/route";
import { api } from "#web/services/api";
import { i18nLike } from "#web/services/i18nLike";
import { useAction } from "@gadgetinc/react";
import { Banner, BlockStack, Box, Button, Card, Icon, InlineStack, ProgressBar, Text } from "@shopify/polaris";
import { ChevronLeftIcon } from "@shopify/polaris-icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { redirect } from "react-router-dom";

import * as s from "./OnboardingSurvey.css";

const en = {
  back: "Back",
  error: {
    description: "Please try again. If the issue persists, please contact support.",
    title: "Failed to load onboarding survey",
  },
  next: "Next",
};

interface OnboardingSurveyProps {
  answers: Record<string, null | string>;
  children: React.ReactNode;
}

const OnboardingSurvey = ({ answers, children }: OnboardingSurveyProps) => {
  const t = i18nLike(en);
  const queryClient = useQueryClient();
  const { data: shopData, isError: shopError, isFetching: shopFetching } = useQuery(ShopQuery());
  const shop = shopData ? makeShop(shopData) : null;

  const [step, setStep] = useState<number>(0);
  const length = useMemo(() => (children ? (Array.isArray(children) ? children.length : 1) : 0), [children]);

  const [, submitOnboardingSurvey] = useAction(api.shopifyShop.submitOnboardingSurvey);
  const [loading, setLoading] = useState(false);

  // Memoize the step validation to avoid recalculating on every render
  const isLastStep = useMemo(() => step === length - 1, [step, length]);
  const canGoBack = useMemo(() => step > 0, [step]);

  // Memoize the validation list to avoid recalculating on every render
  const isValueSetList = useMemo(
    () => [
      Boolean(answers["referrer"]),
      Boolean(answers["revenue"]),
      Boolean(answers["installReason"]),
      Boolean(answers["email"]),
    ],
    [answers],
  );

  const handlePreviousStep = useCallback(() => {
    if (canGoBack) {
      setStep(step - 1);
    }
  }, [canGoBack, step]);

  const handleNextStep = useCallback(async () => {
    if (!isLastStep) {
      setStep(step + 1);
      return;
    }

    if (!shop?.id) return;

    setLoading(true);
    try {
      // Convert answers to a format that matches the API's expected JSONValue type
      const surveyData = {
        email: answers["email"] || "",
        installReason: answers["installReason"] || "",
        referrer: answers["referrer"] || null,
        revenue: answers["revenue"] || null,
      };

      // @ts-expect-error The API expects a specific JSONValue type, but our data structure is valid
      await submitOnboardingSurvey({ id: shop.id, onboardingSurvey: surveyData });
      await queryClient.invalidateQueries({ queryKey: ShopQuery().queryKey });
      redirect("/");
    } catch (error) {
      console.error("kc: error submitting survey", error);
    } finally {
      setLoading(false);
    }
  }, [isLastStep, step, shop?.id, answers, submitOnboardingSurvey, queryClient]);

  if (shopError) {
    return (
      <Banner title={t("error.title")} tone="critical">
        {t("error.description")}
      </Banner>
    );
  }

  if (shopFetching) {
    return <CardSkeleton />;
  }

  return (
    <div className={s.OnboardingSurveyBackgroundContainer}>
      <div className={s.OnboardingSurveyGradientContainer}>
        <Box padding="400">
          <Card padding="1000">
            <div className={s.OnboardingSurveyCardContent}>
              <BlockStack gap="800">
                <BlockStack gap="300">
                  <ProgressBar progress={((step + 1) / length) * 100} size="small" tone="primary" />
                </BlockStack>
                {/* @ts-expect-error unusual usage of children */}
                {children[step]}
              </BlockStack>
              <div className={s.OnboardingSurveyCardFooter}>
                <Button onClick={handlePreviousStep} variant="tertiary" disabled={!canGoBack}>
                  {/* @ts-expect-error Shopify Polaris InlineStack has incorrect type definitions for children */}
                  <InlineStack blockAlign="center" gap="100">
                    <Icon source={ChevronLeftIcon} tone="base" />
                    <Text as="p">{t("back")}</Text>
                  </InlineStack>
                </Button>
                <Button disabled={!isValueSetList[step]} loading={loading} onClick={handleNextStep} variant="primary">
                  {t("next")}
                </Button>
              </div>
            </div>
          </Card>
        </Box>
      </div>
    </div>
  );
};

interface QuestionProps {
  children: React.ReactNode;
  subtitle: string;
  title: string;
}

const Question = ({ children, subtitle, title }: QuestionProps) => {
  return (
    <BlockStack gap="200">
      <Text as="h2" fontWeight="semibold" variant="headingXl">
        {title}
      </Text>
      <Text as="p" tone="subdued">
        {subtitle}
      </Text>
      <Box paddingBlock="400">{children}</Box>
    </BlockStack>
  );
};

OnboardingSurvey.Question = Question;
export default OnboardingSurvey;
